// Work rows as the Assignments they become.
//
// A Work row names work, not a place: the place is the H3 footprint its origin
// record carries, so this is the one read that turns a queued row into an
// assignment target. The target is the whole footprint as one MultiPolygon
// rather than a point inside it — a point chosen from a footprint is a guess
// about which part of the place the technician visits, and the map already draws
// the footprint itself.
//
// Two surfaces author from Work rows — the Work pane's Route shortcut and the
// Assignment form's bulk door — so the read and the geometry live here once, and
// an assignment created either way names the same place. Direct work is not a
// Candidate: it is already an Assignment.

import type { GeoJSON } from "geojson";

import { apiClient } from "@/client";
import { cellBoundaryRing } from "@/lib/h3/cells";
import type {
	LeadDetail,
	OperationsWorkRow,
	WorkOrderDetail,
} from "@/type/api";

/** One Work row as an assignment target. */
export interface WorkTarget {
	row: OperationsWorkRow;
	/** Every cell of the row's footprint, as one geometry. */
	geometry: GeoJSON.MultiPolygon;
}

/** A Work row no assignment can be authored from, and why. */
export interface WorkRefusal {
	row: OperationsWorkRow;
	reason: string;
}

export interface ResolvedWorkTargets {
	targets: WorkTarget[];
	refusals: WorkRefusal[];
}

/**
 * Every cell of a footprint as one MultiPolygon: the target carries the place
 * the district drew, not a point inside it. Each cell's boundary is already a
 * closed [lng, lat] ring in GeoJSON order, so it becomes that polygon's one ring
 * unchanged.
 */
export function footprintGeometry(cells: string[]): GeoJSON.MultiPolygon {
	return {
		type: "MultiPolygon",
		coordinates: cells.map((cell) => [cellBoundaryRing(cell)]),
	};
}

/**
 * The target each Work row would become, in the order the rows were given. A row
 * whose footprint cannot be read is refused with the reason rather than dropped:
 * an assignment created without its place, or a batch that silently holds fewer
 * assignments than the operator checked, would both be worse than the refusal.
 * Direct work is skipped silently — it is already an Assignment, so it is not a
 * candidate at all rather than a candidate that failed.
 */
export async function resolveWorkTargets(
	rows: OperationsWorkRow[],
): Promise<ResolvedWorkTargets> {
	const targets: WorkTarget[] = [];
	const refusals: WorkRefusal[] = [];
	for (const row of rows) {
		if (row.origin === "direct") continue;
		const cells =
			row.origin === "lead"
				? (await apiClient.JSONGet<LeadDetail>(`/api/lead/${row.id}`)).cells
				: (
						await apiClient.JSONGet<WorkOrderDetail>(
							`/api/work-order/${row.id}`,
						)
					).cells;
		if (cells.length === 0) {
			refusals.push({
				row,
				reason: "has no H3 footprint, so it names no place to visit",
			});
			continue;
		}
		targets.push({ row, geometry: footprintGeometry(cells) });
	}
	return { targets, refusals };
}
